export type HGIntervalType = string;
export type HGTicksType = string;

// LIB

export enum HGEra {
  AD = "ad",
  BC = "bc",
}
export interface HGInterval {
  n: bigint;
  it: HGIntervalType;
}
export interface HGIntervalAPI {
  n: string;
  it: HGIntervalType;
  era: HGEra;
}

export interface HGIntervalTick {
  n: bigint;
  duration: bigint;
  tt: HGTicksType;
  offset: bigint;
}

export interface HGSpan {
  begin: HGIntervalTick;
  end: HGIntervalTick;
}

// RECORDS

export interface HGIntervals {
  interval: HGInterval;
  end_interval: HGInterval | null;
  // EDTF-style qualifiers, one pair per endpoint: approximate is "circa" (~),
  // uncertain means the date is doubtful (?); both at once is EDTF's %. They
  // carry no meaning for interval math -- only for how the date reads. Absent
  // is the same as false, so record literals need not spell them out.
  interval_approximate?: boolean;
  interval_uncertain?: boolean;
  end_interval_approximate?: boolean;
  end_interval_uncertain?: boolean;
}

export interface HGRecordUrl {
  label: string;
  url: string;
}

export type HGMediaType = "image" | "audio" | "document";

export interface HGRecordMedia {
  id: string;
  media_type: HGMediaType;
  original_filename: string;
  content_type: string;
  size_bytes: number;
  width: number | null;
  height: number | null;
  caption: string | null;
  position: number;
  created: string;
  url: string;
  thumbnail_url: string | null;
  download_url: string;
}

export interface HGMediaTypeLimits {
  max_count: number;
  max_size_bytes: number;
  allowed_extensions: string[];
}

export interface HGMediaLimits {
  image: HGMediaTypeLimits;
  audio: HGMediaTypeLimits;
  document: HGMediaTypeLimits;
}

export interface HGRecordData {
  title: string;
  content: string;
  url: HGRecordUrl[];
  collection_id: string[];
  // URL name, unique per user. Optional on write: omit to keep the current one
  // (or, on create, to derive it from the title). Always present on read.
  slug?: string | null;
}

export interface HGTimeRecordData extends HGRecordData, HGIntervals {}

export type HGAnyRecordData = HGRecordData | HGTimeRecordData;

export interface HGRecord extends HGRecordData {
  id: string;
  created: number;
  modified: number | null;
  user_id: string;
  // Presigned thumbnail URL of the record's first image (response-only)
  media_thumbnail_url?: string | null;
  // Number of image attachments on the record (response-only)
  media_count?: number;
  // Number of audio attachments on the record (response-only)
  media_audio_count?: number;
  // Number of document attachments on the record (response-only)
  media_document_count?: number;
  // Owner's nickname, for building /{nickname}/-/{slug} (response-only)
  owner_nickname?: string | null;
  // Present when the URL used was stale or non-canonical (response-only)
  canonical_path?: string | null;
  // The collection this record is the title of, if any. Response-only and
  // derived: it is written through the title-record endpoints, never through a
  // record payload, which is why it is not on HGRecordData. Absent on a backend
  // predating the feature, which reads the same as "titles nothing".
  title_collection_id?: string | null;
  // `content` is an excerpt, not the whole thing. Set by the bulk listings,
  // which render two clamped lines per record; anything that shows the full
  // text has to fetch the record by id. Response-only.
  content_truncated?: boolean;
  // Present only on records that do not exist in the database: rows a provider
  // module fetched from an external system for the interval being viewed, and
  // that nothing persists. Its presence is what makes a record read-only --
  // there is no row to edit, no media to load and no permalink to serve.
  external?: HGExternalMeta;
}

export interface HGExternalMeta {
  /** The provider module that produced this row. */
  providerKey: string;
  /** The provider collection it was fetched for. */
  collectionId: string;
  /** The item's own id within the provider, before namespacing. */
  externalId: string;
  /** Link out to the item in the source system. */
  href?: string;
  /**
   * Set when the row stands for a bucket of items rather than one of them.
   * Summaries appear when the visible range is too wide to list -- a month of
   * mail is a count, not four hundred rows -- and carry the bucket's own
   * interval, so the record tree places them with no special case.
   */
  summary?: {
    count: number;
    /** Some providers only offer an approximation, and the row says so. */
    estimate: boolean;
  };
}

export type HGTimeRecord = HGRecord &
  HGIntervals & {
    start_second: bigint;
    end_second: bigint | null;
  };

export interface HGTimeRecordPlus extends HGTimeRecord {
  intervalName: string;
  endIntervalName: string | null;
  intersection: HGIntersection;
}

export type HGAnyRecord = HGTimeRecordPlus | HGRecord;

// API

export type HGTimeRecordSerialized = HGTimeRecordPlus & {
  interval: HGIntervalSerialized;
  end_interval: HGIntervalSerialized | null;
  start_second: string;
  end_second: string | null;
};

export type HGAnyRecordSerialized = HGTimeRecordSerialized | HGRecord;

export interface HGIntervalSerialized {
  n: string;
  it: HGIntervalType;
}

/**
 * A record exactly as the single-record endpoints send it: `GET /records/{id}`,
 * `GET /records/by-slug/...`, and the `title_record` embedded in a collection.
 *
 * Deliberately *not* `HGTimeRecordSerialized`, which it superficially
 * resembles. The wire carries `n` and the second offsets as unsigned
 * magnitudes with the era beside them, the way the database stores them, so
 * reading one straight into a client record turns every BC date into its AD
 * mirror -- 9 April 480 BC comes out 22 September 480. `apiRecordToSerialized`
 * is what closes that gap, and the only thing that should read this type.
 */
export type HGTimeRecordAPI = HGRecord &
  Omit<HGIntervals, "interval" | "end_interval"> & {
    interval: HGIntervalAPI;
    end_interval: HGIntervalAPI | null;
    start_second: string | number;
    end_second: string | number | null;
  };

export type HGAnyRecordAPI = HGTimeRecordAPI | HGRecord;

// MAP

export type HGRecordsMap = {
  [era in HGEra]: {
    [it: HGIntervalType]: {
      [n: string]: {
        records: { finished: HGRecord[]; unfinished: HGRecord[] };
        start_second: string;
        end_second: string;
        end?: HGRecordsMap;
      };
    };
  };
};

// META

export enum HGIntersectionType {
  Contained = 0,
  Exact, // 1
  PartLeft, // 2
  PartRight, // 3
  Transit, // 4
  Starts, // 5
  Ends, // 6
  None, // 7
}

export function intersectionTypeToString(intersectionType: HGIntersectionType) {
  if (intersectionType == HGIntersectionType.Contained) {
    return "Contained";
  } else if (intersectionType == HGIntersectionType.Exact) {
    return "full";
  } else if (intersectionType == HGIntersectionType.PartLeft) {
    return "end";
  } else if (intersectionType == HGIntersectionType.PartRight) {
    return "begin";
  } else if (intersectionType == HGIntersectionType.Transit) {
    return "transit";
  } else if (intersectionType == HGIntersectionType.Starts) {
    return "starts";
  } else if (intersectionType == HGIntersectionType.Ends) {
    return "ends";
  } else if (intersectionType == HGIntersectionType.None) {
    return "None";
  } else {
    return "Unknown";
  }
}

export interface HGIntersectionOffsets {
  begin: number;
  end: number;
  finished: boolean;
}

export interface HGIntersection {
  offsets: HGIntersectionOffsets | null;
  intersectionType: HGIntersectionType;
}

// COLLECTIONS

export interface HGCollectionCreate {
  title: string;
  // Set to make this a provider collection. Its natural subcollections travel
  // in the same request, so a Gmail collection and its categories are created
  // together or not at all.
  provider_key?: string;
  provider_config?: Record<string, unknown>;
  provider_children?: HGProviderChildCreate[];
}

export interface HGCollectionUpdate {
  id: string;
  title: string;
  // Omit to leave the URL name alone; a title edit must not silently rewrite
  // a shared link.
  slug?: string;
}

/** One natural subcollection, created together with its provider root. */
export interface HGProviderChildCreate {
  title: string;
  provider_key: string;
  provider_config: Record<string, unknown>;
}

export interface HGCollection {
  id: string;
  user_id: string;
  title: string;
  // URL name, unique per user. null on rows predating the backfill, in which
  // case URLs fall back to the id.
  slug: string | null;
  // Owner's nickname, or null if they have not chosen one.
  owner_nickname: string | null;
  description: string;
  is_default: boolean;
  is_public: boolean;
  // null = not pinned; otherwise the 0-based order within the user's pins.
  pin_position: number | null;
  created: Date;
  modified: Date | null;
  // Explicit hierarchy: the links the owner configured by hand.
  child_ids: string[];
  parent_ids: string[];
  // Implicit hierarchy, inherited from the title record. Filing a collection's
  // title record into another collection makes this one a child of that one,
  // without anybody linking anything. Kept apart from the explicit arrays so
  // the sidebar can tell the two kinds of children apart. Default to [] on a
  // backend predating the feature.
  implicit_child_ids: string[];
  implicit_parent_ids: string[];
  // The record that *is* this collection, if any. That record deliberately does
  // not belong to the collection it titles.
  title_record_id: string | null;
  // --- external systems ---------------------------------------------------
  // Which provider module renders this collection ("gmail"), or null for an
  // ordinary one. The server stores this opaquely; src/providers/external/
  // holds the only definition of what a key means.
  provider_key?: string | null;
  // Per-collection provider settings -- a Gmail query, a repository id.
  provider_config?: Record<string, unknown>;
  // The grant it fetches with. null means "not connected yet", which is the
  // state the collection page shows a Connect button for.
  connection_id?: string | null;
  // Set on a provider's natural subcollections, naming their root. They are
  // created with it, deleted with it, and exempt from the collection quota.
  provider_root_id?: string | null;
}

/** An OAuth grant on an external system. Never carries a token. */
export interface HGConnection {
  id: string;
  system_id: string;
  // Usually the account's email, so two Google accounts are tellable apart.
  account_label: string | null;
  scopes: string[];
  // "active" | "needs_reauth" | "revoked"
  status: string;
  created: string;
  modified: string | null;
}

export interface HGCollectionProviderUpdate {
  provider_config?: Record<string, unknown>;
  connection_id?: string | null;
  // Distinguishes "leave the connection alone" from "clear it", which a
  // nullable field cannot express on its own.
  set_connection?: boolean;
}

export interface HGCollectionInfo extends HGCollection {
  allowed_children: string[];
  records_count: number;
  // The title record itself. Embedded here rather than fetched separately: the
  // collection page pins it above the timeline, and it is by definition absent
  // from the record list. Only on this model -- one per collection across the
  // whole list response would be a lot of records nobody asked for.
  title_record: HGAnyRecordSerialized | null;
  // Present when the URL used was stale or non-canonical; the page navigates
  // to it with replace:true.
  canonical_path: string | null;
}

// MISC

export interface HGNow {
  [key: HGIntervalType]: bigint;
}

interface HGQuotas {
  collection_slots: number;
  pinned_slots: number;
  can_create_records: boolean;
}

export interface HGNicknameStatus {
  nickname: string | null;
  // null = changeable right now; otherwise an ISO date.
  change_available_at: string | null;
  // Remaining *new* nicknames. Reclaiming one already held is free.
  claims_left: number;
}

export interface HGNicknameAvailability {
  normalized: string | null;
  available: boolean;
  // taken | reserved | too_short | non_ascii | empty
  reason: string | null;
  message: string | null;
}

export interface HGProfile {
  name: string | null;
  email: string | null;
  // A path on this API (/api/user/{id}/avatar) when an avatar is uploaded, or
  // the identity provider's picture URL. Never a presigned storage URL: those
  // expire, and `me` is held for a whole session.
  picture: string | null;
  // What the user set themselves, as opposed to the effective `name`. The
  // settings field binds to this so an inherited name shows as an empty box
  // rather than being saved back as an override.
  display_name: string | null;
}

export interface HGMe extends HGProfile {
  id: string;
  quotas: HGQuotas;
  nickname: HGNicknameStatus;
  is_admin: boolean;
  records_exempt: boolean;
}

export interface HGProfileUpdate {
  display_name: string | null;
}

export interface HGAdminIdentity {
  source: string;
  sub: string;
  name: string | null;
  email: string | null;
  picture: string | null;
  last_login: string | null;
}

export interface HGAdminUser {
  id: string;
  nickname: string | null;
  // The user's own name override; the effective name is this or the first
  // identity's name.
  display_name: string | null;
  has_avatar: boolean;
  is_admin: boolean;
  records_exempt: boolean;
  disabled: boolean;
  collection_slots: number;
  pinned_slots: number;
  // Position in the switcher a signed-out visitor sees; null means the account
  // is not in that list at all.
  builtin_rank: number | null;
  // Password login handle; null if the account has no credentials row.
  username: string | null;
  has_password: boolean;
  identities: HGAdminIdentity[];
}

export interface HGAdminUserDetail extends HGAdminUser {
  collections_count: number;
  records_count: number;
  sessions_count: number;
}

export interface HGAdminUserList {
  items: HGAdminUser[];
  total: number;
  limit: number;
  offset: number;
}

export interface HGAdminUserCreate {
  username: string;
  password: string;
  name?: string;
  email?: string;
  is_admin?: boolean;
  records_exempt?: boolean;
  collection_slots?: number;
  pinned_slots?: number;
}

export interface HGAdminUserPatch {
  // null clears the override, falling back to the provider's name.
  display_name?: string | null;
  is_admin?: boolean;
  records_exempt?: boolean;
  disabled?: boolean;
  collection_slots?: number;
  pinned_slots?: number;
  // null drops the account out of the built-in list.
  builtin_rank?: number | null;
}

export interface HGUser {
  id: string;
  name: string | null;
  email: string | null;
  picture: string | null;
  nickname?: string | null;
  canonical_path?: string | null;
}

// --- Import and export -----------------------------------------------------
// The wire shape of the document format; the server is authoritative (see
// hg/schemas/transfer.py and docs/IMPORT-FORMAT.md). Mirrored here only so the
// dialog can count nodes and show a preview before uploading.

export type HGDateGranularity =
  | "byr"
  | "myr"
  | "kyr"
  | "c"
  | "dec"
  | "y"
  | "m"
  | "w"
  | "d"
  | "h"
  | "min"
  | "s";

export interface HGDateSpec {
  // Signed, with no zero: 1 is 1 AD, -1 is 1 BC.
  year: number;
  granularity: HGDateGranularity;
  month?: number;
  day?: number;
  hour?: number;
  minute?: number;
  second?: number;
}

// What a node becomes. Normally inferred from what the node carries; `both` is
// a record that also heads a collection — a title record.
export type HGImportNodeKind = "collection" | "record" | "both";

// An attachment named by where to fetch it from — the server downloads the URL
// and re-encodes it. Nothing is embedded in the document, which is what keeps
// the 5 MB body cap workable for a tree carrying hundreds of images.
export interface HGImportMedia {
  url: string;
  caption?: string | null;
  // Images only, for now. The server refuses anything else rather than
  // dropping it.
  media_type?: "image";
}

export interface HGImportNode {
  id: string;
  kind?: HGImportNodeKind;
  title: string;
  content?: string;
  slug?: string;
  start?: HGDateSpec;
  end?: HGDateSpec;
  // Written by export for interval types HGDateSpec cannot state (the
  // fractional ones). Not for hand-authoring.
  start_interval?: HGIntervalAPI;
  end_interval?: HGIntervalAPI;
  interval_approximate?: boolean;
  interval_uncertain?: boolean;
  end_interval_approximate?: boolean;
  end_interval_uncertain?: boolean;
  url?: HGRecordUrl[];
  is_public?: boolean;
  children?: HGImportNode[];
  media?: HGImportMedia[] | null;
}

export interface HGImportDocument {
  format: string;
  version: number;
  // The namespace for every node id in the file. Changing it makes a re-import
  // create everything again instead of updating.
  source: string;
  nodes: HGImportNode[];
  exported?: string;
}

export type HGImportAction =
  | "create"
  | "update"
  | "unchanged"
  | "skipped"
  | "failed";

export interface HGImportNodeResult {
  id: string;
  title: string;
  kind: HGImportNodeKind;
  action: HGImportAction;
  record_id?: string | null;
  collection_id?: string | null;
  message?: string | null;
}

export interface HGImportOrphan {
  entity_type: "collection" | "record";
  entity_id: string;
  external_id: string;
  title: string;
  // How many records deleting this collection would take with it — including
  // records filed in other collections too.
  cascade_records: number;
}

export interface HGImportCounts {
  collections_created: number;
  collections_updated: number;
  records_created: number;
  records_updated: number;
  unchanged: number;
  skipped: number;
  failed: number;
  pruned: number;
  // Attachments fetched and stored. On a preview, how many the document lists
  // that are not on their records already.
  media_added: number;
  // Attachments that could not be fetched or decoded. Never fatal.
  media_failed: number;
}

export interface HGImportResult {
  dry_run: boolean;
  source: string;
  target_collection_id: string | null;
  counts: HGImportCounts;
  nodes: HGImportNodeResult[];
  problems: string[];
  orphans: HGImportOrphan[];
}

export interface HGImportRequest {
  document: HGImportDocument;
  target_collection_id?: string | null;
  dry_run?: boolean;
  prune?: boolean;
  // Fetch and attach what the nodes list under `media`. Off by default: it is
  // the one part of an import that makes outbound requests.
  import_media?: boolean;
}

export interface HGExportRequest {
  collection_id?: string | null;
  source?: string;
}

export interface HGExportResult {
  document: HGImportDocument;
  // What the tree shape could not carry. Shown beside the download, not saved.
  problems: string[];
}
